import { useState, useEffect, useCallback } from 'react';
import { useSeller } from '@/components/layout/DashboardLayout';
import { useProfile } from '@/lib/profile-context';
import { sellersAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { Plus, CheckCircle, Clock, AlertTriangle, Circle } from 'lucide-react';
import { toast } from 'sonner';

function getPriorityBadge(p) {
  if (p === 'Critical') return 'bg-[hsl(0,84%,60%)]/15 text-[hsl(0,84%,60%)] border-[hsl(0,84%,60%)]/30';
  if (p === 'High') return 'bg-[hsl(24,94%,56%)]/15 text-[hsl(24,94%,56%)] border-[hsl(24,94%,56%)]/30';
  return 'bg-[hsl(45,93%,58%)]/15 text-[hsl(45,93%,58%)] border-[hsl(45,93%,58%)]/30';
}
function getStatusIcon(s) {
  if (s === 'completed') return <CheckCircle className="w-4 h-4 text-[hsl(142,72%,45%)]" />;
  if (s === 'in_progress') return <Clock className="w-4 h-4 text-[hsl(188,100%,55%)]" />;
  return <Circle className="w-4 h-4 text-muted-foreground" />;
}

export default function TasksPage() {
  const { hasSelection } = useProfile();
  const { currentSellerId, syncVersion, syncing } = useSeller();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [showCreate, setShowCreate] = useState(false);
  const [newTask, setNewTask] = useState({ title: '', priority: 'Medium', assigned_to: '', category: 'General' });

  const loadTasks = useCallback(async () => {
    if (!currentSellerId) { setLoading(false); return; }
    setLoading(true);
    try {
      const res = await sellersAPI.getTasks(currentSellerId, filter);
      setTasks(res.data);
    } catch (err) { console.error(err); } finally { setLoading(false); }
  }, [currentSellerId, filter]);

  useEffect(() => { loadTasks(); }, [loadTasks, syncVersion]);

  const handleCreate = async () => {
    try {
      await sellersAPI.createTask(currentSellerId, newTask);
      toast.success('Task created');
      setShowCreate(false);
      setNewTask({ title: '', priority: 'Medium', assigned_to: '', category: 'General' });
      loadTasks();
    } catch (err) { toast.error('Failed to create task'); }
  };

  const handleStatusChange = async (taskId, newStatus) => {
    try {
      await sellersAPI.updateTask(currentSellerId, taskId, { status: newStatus });
      loadTasks();
    } catch (err) { toast.error('Failed to update'); }
  };

  if (loading || (hasSelection && syncing)) return <div className="space-y-4"><Skeleton className="h-8 w-48" /><Skeleton className="h-96" /></div>;

  const open = tasks.filter(t => t.status === 'open').length;
  const inProgress = tasks.filter(t => t.status === 'in_progress').length;
  const completed = tasks.filter(t => t.status === 'completed').length;

  return (
    <div className="space-y-6" data-testid="tasks-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold">Tasks & Action Items</h1>
          <p className="text-sm text-muted-foreground">Assign and track resolution of compliance issues</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={filter} onValueChange={setFilter}><SelectTrigger className="w-[140px] h-9 text-sm bg-muted/40 border-border/60"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">All Tasks</SelectItem><SelectItem value="open">Open</SelectItem><SelectItem value="in_progress">In Progress</SelectItem><SelectItem value="completed">Completed</SelectItem></SelectContent></Select>
          <Button onClick={() => setShowCreate(true)} data-testid="create-task-button" className="h-9 px-4 text-sm grad-accent text-white border-0 hover:opacity-90"><Plus className="w-4 h-4 mr-1" /> New Task</Button>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <Card className="bg-card border-border/80"><CardContent className="p-4 text-center"><p className="text-3xl font-semibold tabular-nums text-[hsl(0,84%,60%)]">{open}</p><p className="text-xs text-muted-foreground">Open</p></CardContent></Card>
        <Card className="bg-card border-border/80"><CardContent className="p-4 text-center"><p className="text-3xl font-semibold tabular-nums text-[hsl(188,100%,55%)]">{inProgress}</p><p className="text-xs text-muted-foreground">In Progress</p></CardContent></Card>
        <Card className="bg-card border-border/80"><CardContent className="p-4 text-center"><p className="text-3xl font-semibold tabular-nums text-[hsl(142,72%,45%)]">{completed}</p><p className="text-xs text-muted-foreground">Completed</p></CardContent></Card>
      </div>

      <div className="space-y-2">
        {tasks.map(task => (
          <Card key={task.id} className="bg-card border-border/80 hover:border-border transition-colors">
            <CardContent className="p-4">
              <div className="flex items-center gap-4">
                <button onClick={() => handleStatusChange(task.id, task.status === 'completed' ? 'open' : task.status === 'open' ? 'in_progress' : 'completed')}>{getStatusIcon(task.status)}</button>
                <div className="flex-1 min-w-0">
                  <p className={cn('text-sm font-medium', task.status === 'completed' && 'line-through text-muted-foreground')}>{task.title}</p>
                  <div className="flex items-center gap-3 mt-1">
                    <span className="text-xs text-muted-foreground">{task.assigned_to}</span>
                    <Badge variant="outline" className="text-[9px] border-border/60">{task.category}</Badge>
                  </div>
                </div>
                <Badge variant="outline" className={cn('text-[10px] border', getPriorityBadge(task.priority))}>{task.priority}</Badge>
                <Select value={task.status} onValueChange={(v) => handleStatusChange(task.id, v)}><SelectTrigger className="w-[120px] h-8 text-xs bg-muted/20 border-border/40"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="open">Open</SelectItem><SelectItem value="in_progress">In Progress</SelectItem><SelectItem value="completed">Completed</SelectItem></SelectContent></Select>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="bg-card border-border/80">
          <DialogHeader><DialogTitle>Create New Task</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="space-y-2"><Label>Title</Label><Input value={newTask.title} onChange={e => setNewTask({...newTask, title: e.target.value})} placeholder="Task description" className="bg-background/40 border-border" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Priority</Label><Select value={newTask.priority} onValueChange={v => setNewTask({...newTask, priority: v})}><SelectTrigger className="bg-background/40 border-border"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="Critical">Critical</SelectItem><SelectItem value="High">High</SelectItem><SelectItem value="Medium">Medium</SelectItem></SelectContent></Select></div>
              <div className="space-y-2"><Label>Assign To</Label><Input value={newTask.assigned_to} onChange={e => setNewTask({...newTask, assigned_to: e.target.value})} placeholder="Team member" className="bg-background/40 border-border" /></div>
            </div>
            <div className="space-y-2"><Label>Category</Label><Select value={newTask.category} onValueChange={v => setNewTask({...newTask, category: v})}><SelectTrigger className="bg-background/40 border-border"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="General">General</SelectItem><SelectItem value="Claims">Claims</SelectItem><SelectItem value="Shipping">Shipping</SelectItem><SelectItem value="Listings">Listings</SelectItem><SelectItem value="Compliance">Compliance</SelectItem><SelectItem value="Inventory">Inventory</SelectItem><SelectItem value="Customer Service">Customer Service</SelectItem><SelectItem value="Security">Security</SelectItem><SelectItem value="Reviews">Reviews</SelectItem></SelectContent></Select></div>
          </div>
          <DialogFooter><Button onClick={handleCreate} className="grad-accent text-white border-0 hover:opacity-90">Create Task</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
